import React, { useEffect, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useAuth } from '../../hooks/useAuth';
import { UserPersona } from '../../types/cabinet.types';
import { NavTab } from './BottomNavbar';

interface BurgerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  persona: UserPersona;
  activeTab: NavTab;
  onChangeTab: (tab: NavTab) => void;
  /** true якщо пацієнт є в wp_otylosc_patients (програма схуднення) */
  isWeightLoss?: boolean;
}

export const BurgerDrawer: React.FC<BurgerDrawerProps> = ({ 
  isOpen, 
  onClose, 
  persona, 
  activeTab, 
  onChangeTab,
  isWeightLoss
}) => {
  const { t } = useI18n();
  const { logout } = useAuth();
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
    return () => {
        document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  const handleNav = (tab: NavTab) => {
    onChangeTab(tab);
    onClose();
  };

  // A-навігація показується якщо personaType = 'A' АБО якщо пацієнт є в програмі схуднення
  const isPersonaA = persona === 'A' || isWeightLoss === true;

  const navItems = isPersonaA
    ? ([
        { id: 'therapy', label: t('nav_terapia') },
        { id: 'services', label: t('nav_uslugi') },
        { id: 'documents', label: t('nav_doc') },
        { id: 'profile', label: t('nav_dane') }
      ] as const)
    : ([
        { id: 'services', label: t('nav_umow') },
        { id: 'consultations', label: t('nav_cons') },
        { id: 'documents', label: t('nav_doc') },
        { id: 'profile', label: t('nav_dane') }
      ] as const);


  return (
    <div className={`cab-drawer-bg ${isOpen ? 'open' : ''}`} onClick={onClose}>
      <div className="cab-drawer" onClick={e => e.stopPropagation()}>
        <div className="cab-drawer-hdr">
          <button className="cab-drawer-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <nav className="cab-drawer-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`cab-drawer-item ${activeTab === item.id || (item.id === 'therapy' && (activeTab === 'visits' || activeTab === 'visit-summary')) || (item.id === 'consultations' && activeTab === 'visit-summary') ? 'active' : ''}`}
              onClick={() => handleNav(item.id as NavTab)}
            >
              {item.label}
              {(activeTab === item.id || (item.id === 'therapy' && (activeTab === 'visits' || activeTab === 'visit-summary')) || (item.id === 'consultations' && activeTab === 'visit-summary')) && (
                <span className="cab-drawer-arr">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="cab-drawer-foot">
          <button className="cab-logout-btn" onClick={logout}>
            {t('logout')}
          </button>
        </div>
      </div>
      <style>{`
        .cab-drawer-bg {
          position: fixed;
          inset: 0;
          background: rgba(8, 10, 41, 0.4);
          backdrop-filter: blur(3px);
          z-index: 100;
          display: flex;
          justify-content: flex-end;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .cab-drawer-bg.open {
          opacity: 1;
        }
        .cab-drawer {
          width: 85%;
          max-width: 380px;
          height: 100%;
          background: #fff;
          transform: translateX(100%);
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          display: flex;
          flex-direction: column;
        }
        .cab-drawer-bg.open .cab-drawer {
          transform: translateX(0);
        }
        .cab-drawer-hdr {
          display: flex;
          justify-content: flex-end;
          padding: 20px;
        }
        .cab-drawer-close {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          background: var(--cab-light);
          color: var(--cab-violet);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .cab-drawer-nav {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 0 24px;
        }
        .cab-drawer-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 0;
          border: none;
          background: none;
          font-size: 18px;
          font-weight: 600;
          color: #222;
          font-family: inherit;
          text-align: left;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
        }
        .cab-drawer-item.active {
          color: var(--cab-violet);
        }
        .cab-drawer-arr {
          color: var(--cab-violet);
        }
        .cab-drawer-foot {
          padding: 24px;
          margin-bottom: env(safe-area-inset-bottom, 20px);
        }
        .cab-logout-btn {
          width: 100%;
          height: 54px;
          border-radius: 14px;
          border: 1.5px solid #E6E1F0;
          background: #fff;
          color: var(--cab-error);
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          font-family: inherit;
        }
      `}</style>
    </div>
  );
};
